// =============================================
// Parent Messages Page
// Kipikid - מערכת נוכחות ילדים בגנים
// הודעות מהגן להורים - שליפה מ-API
// חיפוש + תזכורות
// =============================================

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Bell,
  CheckCheck,
  Clock,
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
  Search,
  BellRing,
  Trash2
} from 'lucide-react';
import { notificationsApi, UserNotification } from '../../services/api';
import { parentColors } from '../../styles/colors';

// Colors
const colors = {
  ...parentColors,
  info: '#3b82f6',
  infoLight: '#dbeafe',
};

// מפתח localStorage לתזכורות
const REMINDERS_KEY = 'kipikid-message-reminders';

// ממשק תזכורת
interface MessageReminder {
  notificationPublicId: string;
  reminderDate: string; // ISO date string (YYYY-MM-DD)
  title: string;
  createdAt: string;
}

// פונקציות עזר לתזכורות
function getReminders(): MessageReminder[] {
  try {
    const stored = localStorage.getItem(REMINDERS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

function saveReminders(reminders: MessageReminder[]) {
  localStorage.setItem(REMINDERS_KEY, JSON.stringify(reminders));
}

function addReminder(reminder: MessageReminder) {
  const reminders = getReminders().filter(r => r.notificationPublicId !== reminder.notificationPublicId);
  reminders.push(reminder);
  saveReminders(reminders);
}

function removeReminder(notificationPublicId: string) {
  const reminders = getReminders().filter(r => r.notificationPublicId !== notificationPublicId);
  saveReminders(reminders);
}

function getReminder(notificationPublicId: string): MessageReminder | undefined {
  return getReminders().find(r => r.notificationPublicId === notificationPublicId);
}

// בדיקת תזכורות שהגיע זמנן
function getDueReminders(): MessageReminder[] {
  const today = new Date().toISOString().split('T')[0];
  return getReminders().filter(r => r.reminderDate <= today);
}

// פורמט תאריך יחסי
function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'היום';
  if (diffDays === 1) return 'אתמול';
  if (diffDays < 7) return `לפני ${diffDays} ימים`;

  return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// פורמט שעה
function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
}

export default function ParentMessages() {
  const [filter, setFilter] = useState<'all' | 'unread' | 'reminders'>('all');
  const [selectedMessage, setSelectedMessage] = useState<UserNotification | null>(null);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dueReminders, setDueReminders] = useState<MessageReminder[]>([]);
  const [remindersVersion, setRemindersVersion] = useState(0); // trigger re-render on reminder changes

  // טעינת הודעות מה-API
  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await notificationsApi.getNotifications(1, 100);

      if (response.success && response.data) {
        // סינון רק הודעות מהגן (KindergartenMessage)
        const kindergartenMessages = response.data.filter(
          n => n.type === 'KindergartenMessage'
        );
        setNotifications(kindergartenMessages);

        if (kindergartenMessages.length === 0 && response.data.length > 0) {
          console.log('API returned messages but none are KindergartenMessage type. Types found:',
            [...new Set(response.data.map(n => n.type))]);
        }
      } else {
        console.error('API response not successful:', response);
        setError(response.message || 'שגיאה בטעינת הודעות');
      }
    } catch (err: unknown) {
      console.error('Error loading notifications:', err);
      const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosError?.response?.status === 401) {
        setError('פג תוקף ההתחברות. נא להתנתק ולהתחבר מחדש.');
      } else {
        setError('שגיאה בטעינת הודעות. נסה שוב מאוחר יותר.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // בדיקת תזכורות שהגיע זמנן
  useEffect(() => {
    setDueReminders(getDueReminders());
  }, [remindersVersion, notifications]);

  // סימון הודעה כנקראה
  const handleMessageClick = async (message: UserNotification) => {
    setSelectedMessage(message);

    // אם ההודעה לא נקראה, סמן אותה כנקראה
    if (!message.isRead) {
      try {
        const response = await notificationsApi.markAsRead(message.publicId);
        if (response.success) {
          // עדכון מקומי
          setNotifications(prev =>
            prev.map(n =>
              n.publicId === message.publicId
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            )
          );
        }
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }
  };

  // חיפוש - סינון לפי תוכן, כותרת, שם ילד
  const searchFilter = (message: UserNotification): boolean => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (
      (message.title || '').toLowerCase().includes(q) ||
      (message.body || '').toLowerCase().includes(q) ||
      (message.metadata?.childName || '').toLowerCase().includes(q) ||
      (message.metadata?.kindergartenName || '').toLowerCase().includes(q)
    );
  };

  // סינון כפול: filter tab + חיפוש
  const filteredMessages = notifications
    .filter(m => {
      if (filter === 'unread') return !m.isRead;
      if (filter === 'reminders') {
        const reminder = getReminder(m.publicId);
        return !!reminder;
      }
      return true;
    })
    .filter(searchFilter);

  const unreadCount = notifications.filter(m => !m.isRead).length;
  const remindersCount = getReminders().filter(r =>
    notifications.some(n => n.publicId === r.notificationPublicId)
  ).length;

  // בדיקה אם הודעה היא כללית (לכל הגן)
  const isGeneralMessage = (message: UserNotification): boolean => {
    return message.metadata?.isGeneral === true || !message.metadata?.childName;
  };

  // הוספת/הסרת תזכורת
  const toggleReminder = (message: UserNotification, reminderDate: string) => {
    addReminder({
      notificationPublicId: message.publicId,
      reminderDate,
      title: message.title,
      createdAt: new Date().toISOString(),
    });
    setRemindersVersion(v => v + 1);
  };

  const handleRemoveReminder = (notificationPublicId: string) => {
    removeReminder(notificationPublicId);
    setRemindersVersion(v => v + 1);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text }}>
            הודעות מהגן
          </h1>
          {unreadCount > 0 && (
            <p style={{ fontSize: '14px', color: colors.textLight, marginTop: '4px' }}>
              {unreadCount} הודעות חדשות
            </p>
          )}
        </div>
        <motion.button
          onClick={loadNotifications}
          disabled={isLoading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: colors.secondaryLight,
            color: colors.secondary,
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
          }}
          whileHover={!isLoading ? { scale: 1.02 } : {}}
          whileTap={!isLoading ? { scale: 0.98 } : {}}
        >
          {isLoading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={18} />}
          רענון
        </motion.button>
      </div>

      {/* התראה על תזכורות שהגיע זמנן */}
      {dueReminders.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            backgroundColor: colors.warningLight,
            borderRadius: '12px',
            padding: '12px 16px',
            border: `2px solid ${colors.warning}`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <BellRing size={20} color={colors.warning} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: 0 }}>
              יש לך {dueReminders.length} תזכורות להיום!
            </p>
            <p style={{ fontSize: '12px', color: colors.textLight, margin: '2px 0 0 0' }}>
              לחץ על &quot;תזכורות&quot; למטה לצפייה
            </p>
          </div>
          <motion.button
            onClick={() => setFilter('reminders')}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              backgroundColor: colors.warning,
              color: 'white',
              border: 'none',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
            whileTap={{ scale: 0.95 }}
          >
            צפה
          </motion.button>
        </motion.div>
      )}

      {/* Search Bar - חיפוש */}
      <div style={{ position: 'relative' }}>
        <Search
          size={18}
          color={colors.textLight}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        />
        <input
          type="text"
          placeholder="חיפוש לפי תוכן, שם ילד או שם הגן..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 40px 12px 12px',
            borderRadius: '12px',
            border: `2px solid ${colors.border}`,
            fontSize: '14px',
            color: colors.text,
            backgroundColor: 'white',
            boxSizing: 'border-box',
            outline: 'none',
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = colors.secondary; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = colors.border; }}
        />
        {searchQuery && (
          <motion.button
            onClick={() => setSearchQuery('')}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: colors.border,
              border: 'none',
              borderRadius: '50%',
              width: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <X size={12} color={colors.textLight} />
          </motion.button>
        )}
      </div>

      {/* Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '6px',
        backgroundColor: colors.border,
        padding: '4px',
        borderRadius: '12px'
      }}>
        <FilterTab
          label="כל ההודעות"
          isActive={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <FilterTab
          label={`לא נקראו (${unreadCount})`}
          isActive={filter === 'unread'}
          onClick={() => setFilter('unread')}
        />
        <FilterTab
          label={`תזכורות${remindersCount > 0 ? ` (${remindersCount})` : ''}`}
          isActive={filter === 'reminders'}
          onClick={() => setFilter('reminders')}
          icon={<BellRing size={14} />}
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: colors.textLight,
        }}>
          <Loader2 size={48} style={{ margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
          <p>טוען הודעות...</p>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: colors.danger,
          backgroundColor: colors.dangerLight,
          borderRadius: '12px',
        }}>
          <AlertCircle size={48} style={{ margin: '0 auto 16px' }} />
          <p>{error}</p>
          <button
            onClick={loadNotifications}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: colors.danger,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            נסה שוב
          </button>
        </div>
      )}

      {/* Messages List */}
      {!isLoading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* תוצאות חיפוש */}
          {searchQuery && (
            <p style={{ fontSize: '13px', color: colors.textLight }}>
              {filteredMessages.length > 0
                ? `נמצאו ${filteredMessages.length} תוצאות עבור "${searchQuery}"`
                : `לא נמצאו תוצאות עבור "${searchQuery}"`}
            </p>
          )}

          {filteredMessages.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: colors.textLight,
            }}>
              <MessageSquare size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <p>
                {searchQuery
                  ? 'לא נמצאו הודעות התואמות לחיפוש'
                  : filter === 'unread'
                    ? 'אין הודעות שלא נקראו'
                    : filter === 'reminders'
                      ? 'אין תזכורות פעילות'
                      : 'אין הודעות'}
              </p>
            </div>
          ) : (
            filteredMessages.map((message, index) => (
              <MessageCard
                key={message.publicId}
                message={message}
                index={index}
                isGeneral={isGeneralMessage(message)}
                onClick={() => handleMessageClick(message)}
                reminder={getReminder(message.publicId)}
                searchQuery={searchQuery}
              />
            ))
          )}
        </div>
      )}

      {/* Message Detail Modal */}
      <AnimatePresence>
        {selectedMessage && (
          <MessageDetailModal
            message={selectedMessage}
            isGeneral={isGeneralMessage(selectedMessage)}
            onClose={() => setSelectedMessage(null)}
            reminder={getReminder(selectedMessage.publicId)}
            onSetReminder={(date) => toggleReminder(selectedMessage, date)}
            onRemoveReminder={() => {
              handleRemoveReminder(selectedMessage.publicId);
            }}
          />
        )}
      </AnimatePresence>

      {/* CSS for animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Filter Tab Component
function FilterTab({ label, isActive, onClick, icon }: { label: string; isActive: boolean; onClick: () => void; icon?: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '10px 8px',
        backgroundColor: isActive ? 'white' : 'transparent',
        color: isActive ? colors.text : colors.textLight,
        border: 'none',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: isActive ? '600' : '400',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

// Highlight search text helper
function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim() || !text) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} style={{ backgroundColor: colors.warningLight, color: colors.text, padding: '0 1px', borderRadius: '2px' }}>{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

// Message Card Component
function MessageCard({
  message,
  index,
  isGeneral,
  onClick,
  reminder,
  searchQuery,
}: {
  message: UserNotification;
  index: number;
  isGeneral: boolean;
  onClick: () => void;
  reminder?: MessageReminder;
  searchQuery: string;
}) {
  const Icon = isGeneral ? Bell : MessageSquare;
  const cardColor = isGeneral ? colors.info : colors.secondary;
  const cardBgColor = isGeneral ? colors.infoLight : (message.isRead ? 'white' : colors.secondaryLight);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      onClick={onClick}
      style={{
        backgroundColor: cardBgColor,
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        cursor: 'pointer',
        border: message.isRead ? `1px solid ${colors.border}` : `2px solid ${cardColor}`,
        position: 'relative',
      }}
    >
      {!message.isRead && (
        <div style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          width: '10px',
          height: '10px',
          backgroundColor: cardColor,
          borderRadius: '50%',
        }} />
      )}

      {/* תזכורת badge */}
      {reminder && (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: message.isRead ? '8px' : '32px',
          backgroundColor: colors.warningLight,
          border: `1px solid ${colors.warning}`,
          borderRadius: '6px',
          padding: '2px 6px',
          fontSize: '10px',
          fontWeight: '600',
          color: colors.warning,
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
        }}>
          <BellRing size={10} />
          {new Date(reminder.reminderDate).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        {/* Icon */}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          backgroundColor: isGeneral ? colors.infoLight : colors.secondaryLight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={22} color={cardColor} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
            <p style={{ fontSize: '13px', color: colors.textLight }}>
              {isGeneral
                ? <HighlightText text={message.metadata?.kindergartenName || 'הגן'} query={searchQuery} />
                : <>
                    <HighlightText text={message.metadata?.childName || ''} query={searchQuery} />
                    {' | '}
                    <HighlightText text={message.metadata?.kindergartenName || 'הגן'} query={searchQuery} />
                  </>
              }
            </p>
            <span style={{ fontSize: '12px', color: colors.textLight, whiteSpace: 'nowrap', marginRight: '8px' }}>
              {formatRelativeDate(message.createdAt)}
            </span>
          </div>
          <h3 style={{
            fontSize: '15px',
            fontWeight: message.isRead ? '500' : '600',
            color: colors.text,
            marginBottom: '4px',
          }}>
            <HighlightText text={message.title} query={searchQuery} />
          </h3>
          {/* תצוגה מקוצרת - לחץ לקריאה */}
          <p style={{
            fontSize: '13px',
            color: message.isRead ? colors.textLight : colors.secondary,
            fontStyle: 'italic',
          }}>
            {message.isRead ? '✓ נקרא - לחץ לצפייה' : '📩 לחץ לקריאת ההודעה'}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// Message Detail Modal
function MessageDetailModal({
  message,
  isGeneral,
  onClose,
  reminder,
  onSetReminder,
  onRemoveReminder,
}: {
  message: UserNotification;
  isGeneral: boolean;
  onClose: () => void;
  reminder?: MessageReminder;
  onSetReminder: (date: string) => void;
  onRemoveReminder: () => void;
}) {
  const Icon = isGeneral ? Bell : MessageSquare;
  const cardColor = isGeneral ? colors.info : colors.secondary;
  const [showReminderPicker, setShowReminderPicker] = useState(false);

  // חישוב תאריכי תזכורת מהירים
  const today = new Date();
  const quickReminders = [
    { label: 'מחר', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString().split('T')[0] },
    { label: 'בעוד 3 ימים', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3).toISOString().split('T')[0] },
    { label: 'בעוד שבוע', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7).toISOString().split('T')[0] },
  ];

  const handleQuickReminder = (date: string) => {
    onSetReminder(date);
    setShowReminderPicker(false);
  };

  const handleCustomDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      onSetReminder(e.target.value);
      setShowReminderPicker(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        style={{
          position: 'relative',
          backgroundColor: isGeneral ? colors.infoLight : 'white',
          borderRadius: '24px 24px 0 0',
          padding: '24px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={20} color="#6b7280" />
        </button>

        {/* Handle */}
        <div style={{ width: '40px', height: '4px', backgroundColor: '#e5e7eb', borderRadius: '2px', margin: '0 auto 20px' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: isGeneral ? colors.infoLight : colors.secondaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: isGeneral ? `2px solid ${colors.info}` : 'none',
          }}>
            <Icon size={24} color={cardColor} />
          </div>
          <div>
            <p style={{ fontSize: '13px', color: colors.textLight }}>
              {isGeneral
                ? (message.metadata?.kindergartenName || 'הגן')
                : `${message.metadata?.childName || ''} | ${message.metadata?.kindergartenName || 'הגן'}`
              }
            </p>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>{message.title}</h2>
          </div>
        </div>

        {/* Time */}
        <p style={{
          fontSize: '13px',
          color: colors.textLight,
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <Clock size={14} />
          {formatRelativeDate(message.createdAt)} בשעה {formatTime(message.createdAt)}
        </p>

        {/* Content */}
        <div style={{
          backgroundColor: isGeneral ? 'white' : '#f9fafb',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '15px',
          lineHeight: 1.6,
          color: colors.text,
          marginBottom: '16px',
        }}>
          {message.body}
        </div>

        {/* Reminder Section */}
        <div style={{
          backgroundColor: reminder ? colors.warningLight : '#f9fafb',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '16px',
          border: reminder ? `1px solid ${colors.warning}` : `1px solid ${colors.border}`,
        }}>
          {reminder ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BellRing size={18} color={colors.warning} />
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: colors.text, margin: 0 }}>
                    תזכורת נקבעה
                  </p>
                  <p style={{ fontSize: '12px', color: colors.textLight, margin: '2px 0 0 0' }}>
                    {new Date(reminder.reminderDate).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <motion.button
                onClick={onRemoveReminder}
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.danger}`,
                  backgroundColor: 'white',
                  color: colors.danger,
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                whileTap={{ scale: 0.95 }}
              >
                <Trash2 size={12} />
                הסר
              </motion.button>
            </div>
          ) : (
            <>
              <motion.button
                onClick={() => setShowReminderPicker(!showReminderPicker)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: colors.textLight,
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                whileTap={{ scale: 0.98 }}
              >
                <BellRing size={16} />
                {showReminderPicker ? 'בטל' : 'הזכר לי'}
              </motion.button>

              <AnimatePresence>
                {showReminderPicker && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      marginTop: '10px',
                      paddingTop: '10px',
                      borderTop: `1px solid ${colors.border}`,
                    }}>
                      {/* כפתורי תזכורת מהירים */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {quickReminders.map(qr => (
                          <motion.button
                            key={qr.label}
                            onClick={() => handleQuickReminder(qr.date)}
                            style={{
                              flex: 1,
                              padding: '8px 4px',
                              borderRadius: '8px',
                              border: `1px solid ${colors.secondary}`,
                              backgroundColor: colors.secondaryLight,
                              color: colors.secondary,
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {qr.label}
                          </motion.button>
                        ))}
                      </div>
                      {/* בחירת תאריך מותאם */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: colors.textLight, whiteSpace: 'nowrap' }}>תאריך אחר:</span>
                        <input
                          type="date"
                          min={new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString().split('T')[0]}
                          onChange={handleCustomDate}
                          style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '8px',
                            border: `1px solid ${colors.border}`,
                            fontSize: '13px',
                            color: colors.text,
                            backgroundColor: 'white',
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>

        {/* Status */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '13px',
          color: colors.success,
        }}>
          <CheckCheck size={16} />
          נקרא
        </div>
      </motion.div>
    </motion.div>
  );
}
